/**
 * From a failure to a sentence an architect can act on (`KLQ-20260819-005`).
 *
 * Same rule as every other `problems.ts` here: branch on the problem's `code`, never on the
 * status — the contract `backend/src/kalqa/core/errors.py` states in its first paragraph.
 *
 * This screen has two error vocabularies rather than one, and keeping them apart is the point:
 *
 * - **The API's**, for the calls that create, publish and roll back. `409` on a publish means
 *   the upload was requested as the wrong kind, or was already attached — a state conflict,
 *   not a permission problem.
 * - **The upload pipeline's** (`upload.ts`), which fails in ways HTTP never sees: an insecure
 *   context with no `crypto.subtle`, a transfer that died mid-flight, a scanner verdict. Each
 *   needs a different next action from the person, so each gets its own sentence rather than
 *   one "upload failed" that leaves them guessing whether to retry.
 */

import { ApiError, type FieldError } from '@/api/client';
import type { MessageKeys } from '@/lib/i18n';

import { UploadError } from './upload';

type DrawingsKey = MessageKeys['drawings'];

/**
 * Codes from `ErrorCode` in `backend/src/kalqa/core/errors.py`.
 *
 * TWIN: that enum. `not_found` is the interesting one on this screen: a slot outside the
 * caller's zones answers `404` byte-identically to one that never existed
 * (`hierarchy.scope`), so the copy says "no longer exists" and never hints at a permission —
 * the server was careful not to, and a client that guesses undoes that care.
 */
const BY_CODE: Readonly<Record<string, DrawingsKey>> = {
  forbidden: 'error.forbidden',
  not_found: 'error.gone',
  conflict: 'error.conflict',
  payload_too_large: 'error.tooLarge',
  rate_limited: 'error.rateLimited',
  validation_failed: 'error.validation',
  idempotency_key_reused: 'error.retryEdited',
  csrf_failed: 'error.unexpected',
};

/** The message key for any API failure on this screen. */
export function apiErrorKey(error: unknown): DrawingsKey {
  if (!(error instanceof ApiError)) return 'error.unexpected';
  return BY_CODE[error.code] ?? 'error.unexpected';
}

/**
 * The message key for a failed upload — the pipeline's own failures first, then the API's.
 *
 * `rejected` covers both verdicts that stop a publish: a file the sniffer refused (wrong
 * format behind a `.pdf` name) and one the scanner quarantined. They are one sentence because
 * the action is the same — that file cannot go in — and two would invite reading the
 * difference as a hint about the contents.
 */
export function uploadErrorKey(error: unknown): DrawingsKey {
  if (error instanceof UploadError) {
    switch (error.failure) {
      case 'insecure_context':
        return 'upload.error.insecure';
      case 'hash_failed':
        return 'upload.error.unreadable';
      case 'transfer_failed':
        return 'upload.error.transfer';
      case 'rejected':
        return 'upload.error.rejected';
      case 'timed_out':
        return 'upload.error.slow';
    }
  }
  if (error instanceof ApiError && error.code === 'validation_failed') {
    // The one API failure the upload path produces that is about the *file*: the signed PUT
    // is issued for an exact size and type, so a mismatch is refused before any byte moves.
    return 'upload.error.refused';
  }
  return apiErrorKey(error);
}

/**
 * True for any domain-state `409` — among them a create or a rename whose code a sibling
 * already holds, where the `detail` names that sibling (owner, 2026-09-15).
 *
 * `isScopeConflict` used to sit beside it for the removal that would strand a scoped member.
 * That removal no longer answers `409` — the stranded member loses the project instead
 * (`KLQ-20261028-008`) — so the case and its copy went with it.
 */
export function isConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'conflict';
}

/** The server's own sentence, when it wrote one worth showing verbatim. */
export function detailOf(error: unknown): string | null {
  if (error instanceof ApiError) return error.problem.detail ?? null;
  return null;
}

/**
 * Every field error a failure carries, in the order the server listed them.
 *
 * `ApiError.fieldErrors()` keys by pointer and so silently drops the second of two entries
 * that name the same location. That is the wrong shape for a refusal whose whole point is
 * that *two* things disagree, so this returns the list as sent.
 */
export function fieldErrorsOf(error: unknown): readonly FieldError[] {
  if (!(error instanceof ApiError)) return [];
  return error.problem.errors ?? [];
}

/**
 * A generation refusal, placed on the form that caused it (`KLQ-20260827-004`).
 *
 * The server addresses a level plan by JSON Pointer — `/levels/2/kind`,
 * `/levels/2/slots/0` — which is `LevelPlanFields`' own coordinate system and not a new one.
 * This turns those strings into the two lookups the form needs, and keeps a third bucket for
 * the ones it does not understand.
 *
 * **`unplaced` is the reason this is not a `Map` and nothing else.** A build of this app that
 * meets a pointer shape added to the server after it shipped must show the person *something*
 * — a pointer nobody rendered and nobody reported is a refusal that looks like a success with
 * a blank screen. Anything unplaced falls back to the banner, which is where it would have
 * been anyway before any of this existed.
 */
export interface PlanFieldErrors {
  /** Keyed by level index. Everything addressed at the level rather than at one of its slots. */
  readonly byLevel: ReadonlyMap<number, readonly FieldError[]>;
  /** Keyed by `${levelIndex}:${slotIndex}` — the position in that level's `slots` array. */
  readonly bySlot: ReadonlyMap<string, readonly FieldError[]>;
  /** Pointers this build does not place. Render them in the banner. */
  readonly unplaced: readonly FieldError[];
  /** The level to bring into view, or `null` when nothing was placed. */
  readonly firstLevel: number | null;
}

/** The key `bySlot` uses. Exported so a caller cannot spell it differently. */
export function slotKey(levelIndex: number, slotIndex: number): string {
  return `${String(levelIndex)}:${String(slotIndex)}`;
}

const EMPTY_PLAN_ERRORS: PlanFieldErrors = {
  byLevel: new Map(),
  bySlot: new Map(),
  unplaced: [],
  firstLevel: null,
};

function push<K>(into: Map<K, FieldError[]>, key: K, value: FieldError): void {
  const existing = into.get(key);
  if (existing === undefined) into.set(key, [value]);
  else existing.push(value);
}

export function planFieldErrors(error: unknown): PlanFieldErrors {
  const errors = fieldErrorsOf(error);
  if (errors.length === 0) return EMPTY_PLAN_ERRORS;

  const byLevel = new Map<number, FieldError[]>();
  const bySlot = new Map<string, FieldError[]>();
  const unplaced: FieldError[] = [];
  let firstLevel: number | null = null;

  for (const entry of errors) {
    // `/levels/2/slots/0` → ['', 'levels', '2', 'slots', '0']. Anything that does not start
    // this way belongs to a form other than this one, and is not this component's to guess at.
    const parts = entry.field.split('/');
    const index = Number(parts[2]);
    if (parts[1] !== 'levels' || !Number.isInteger(index) || index < 0) {
      unplaced.push(entry);
      continue;
    }
    if (firstLevel === null || index < firstLevel) firstLevel = index;
    const slotIndex = parts[3] === 'slots' ? Number(parts[4]) : Number.NaN;
    if (Number.isInteger(slotIndex) && slotIndex >= 0) {
      push(bySlot, slotKey(index, slotIndex), entry);
    } else {
      push(byLevel, index, entry);
    }
  }

  return { byLevel, bySlot, unplaced, firstLevel };
}
